import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { RatingScalePageStyles as S } from '~/pages/communication/libs/RatingScalePage/styles'
import { useDelayedContinue } from '~/shared/hooks/useDelayedContinue'
import { useNextStep } from '~/shared/hooks/useNextStep'
import { TPageProps } from '~/shared/types/commonTypes'

const RATING_OPTIONS = [1, 2, 3, 4, 5] as const

interface IRatingScalePageProps extends TPageProps {
  question: string
  titleI18nKey: string
  subtitleI18nKey: string
  labelWeakI18nKey: string
  labelPowerfulI18nKey: string
  imagePng: string
  imageWebp: string
  imageAlt: string
  ns?: string
  contentGap?: number
}

const RatingScalePage = ({
  nextPagePath,
  pageId,
  question,
  titleI18nKey,
  subtitleI18nKey,
  labelWeakI18nKey,
  labelPowerfulI18nKey,
  imagePng,
  imageWebp,
  imageAlt,
  ns = 'communication',
  contentGap,
}: IRatingScalePageProps) => {
  const { t } = useTranslation(ns)
  const [answer, setAnswer] = useState<number | null>(null)
  const { isAnswersDisabled, handleChange, setIsAnswersDisabled } =
    useDelayedContinue()

  const handleContinue = useNextStep({
    nextPagePath,
    pageId,
    pageName: pageId,
    question,
  })

  return (
    <S.Root>
      <S.ContentWrapper $gap={contentGap}>
        <S.TitleGroup>
          <S.Title>
            <Trans ns={ns} i18nKey={titleI18nKey} />
          </S.Title>
          <S.Subtitle>
            <Trans ns={ns} i18nKey={subtitleI18nKey} />
          </S.Subtitle>
        </S.TitleGroup>

        <S.ImageWrapper>
          <picture>
            <source srcSet={imageWebp} type="image/webp" />
            <img src={imagePng} alt={imageAlt} />
          </picture>
        </S.ImageWrapper>

        <S.RatingBlock>
          <S.RatingButtons>
            {RATING_OPTIONS.map((value) => (
              <S.RatingButton
                key={value}
                type="button"
                $isSelected={answer === value}
                disabled={isAnswersDisabled}
                onClick={() => {
                  if (isAnswersDisabled) return
                  setAnswer(value)
                  handleChange(String(value), handleContinue)
                  setIsAnswersDisabled(true)
                }}
              >
                {value}
              </S.RatingButton>
            ))}
          </S.RatingButtons>

          <S.RatingLabels>
            <S.RatingLabel>{t(labelWeakI18nKey)}</S.RatingLabel>
            <S.ArrowLine />
            <S.RatingLabel>{t(labelPowerfulI18nKey)}</S.RatingLabel>
          </S.RatingLabels>
        </S.RatingBlock>
      </S.ContentWrapper>
    </S.Root>
  )
}

export default RatingScalePage
